/**
 * PressureHeatmap - Canvas 2D pressure heatmap with LUT rendering.
 *
 * Renders two insole-shaped heatmaps (left foot, right foot) vertically.
 * Each foot's 86-point pressure sensor array is rendered as overlapping
 * radial-gradient blobs colored via the selected LUT. A legend at the bottom
 * shows the pressure gradient with 4 color stops, and a white COP dot marks
 * the pressure center for each foot.
 *
 * Performance optimizations (Phase 7.3):
 *   - LUT cached at module level (avoids 256-entry rebuild per frame).
 *   - Insole template (background + outline) pre-rendered to an offscreen
 *     canvas and reused across frames — only pressure points are redrawn.
 *   - requestAnimationFrame batching: multiple prop updates in the same frame
 *     coalesce into a single render (latest state wins).
 *   - No per-frame array allocations: right-foot Y mirroring is done via a
 *     flag, and COP is computed from the original points without copying.
 *
 * Performance target: single-frame render P95 ≤ 8ms.
 */
import { useEffect, useRef } from 'react';
import type { DualPressureArray, PressurePoint } from '../../types/ski-data';
import { ColorSchemeId } from '../../types/enums';
import {
  getLUTForScheme,
  getColorFromLUT,
} from '../../utils/color-lut';
import { calculateCop } from '../../utils/cop-calculator';
import { PRESSURE_INSOLE_BOUNDS } from '../../data/pressure-coordinates';
import { COLOR_SCHEMES } from '../../constants/design-tokens';

export interface PressureHeatmapProps {
  pressureArray: DualPressureArray | null;
  colorScheme: ColorSchemeId;
  width: number;
  height: number;
}

// Insole coordinate bounds in the format expected by renderPressureHeatmap.
const INSOLE_BOUNDS = {
  minX: PRESSURE_INSOLE_BOUNDS.xMin,
  maxX: PRESSURE_INSOLE_BOUNDS.xMax,
  minY: PRESSURE_INSOLE_BOUNDS.yMin,
  maxY: PRESSURE_INSOLE_BOUNDS.yMax,
};

// ---------------------------------------------------------------------------
// Module-level caches (avoid per-frame allocations)
// ---------------------------------------------------------------------------

/** LUT cache: avoids rebuilding the 256-entry color table every frame. */
const lutCache = new Map<ColorSchemeId, Uint8ClampedArray>();

function getCachedLUT(scheme: ColorSchemeId): Uint8ClampedArray {
  let lut = lutCache.get(scheme);
  if (!lut) {
    lut = getLUTForScheme(scheme);
    lutCache.set(scheme, lut);
  }
  return lut;
}

/** Offscreen canvas template cache: stores background + outline per foot size. */
const templateCache = new Map<string, HTMLCanvasElement>();

/** Compute the coordinate layout for a foot region. */
function getInsoleLayout(footWidth: number, footHeight: number) {
  const scaleX = footWidth / (INSOLE_BOUNDS.maxX - INSOLE_BOUNDS.minX);
  const scaleY = footHeight / (INSOLE_BOUNDS.maxY - INSOLE_BOUNDS.minY);
  const scale = Math.min(scaleX, scaleY);
  const offsetX = (footWidth - (INSOLE_BOUNDS.maxX - INSOLE_BOUNDS.minX) * scale) / 2;
  const offsetY = (footHeight - (INSOLE_BOUNDS.maxY - INSOLE_BOUNDS.minY) * scale) / 2;
  const padding = 4;
  return { scale, offsetX, offsetY, padding };
}

/**
 * Build (or retrieve from cache) an offscreen canvas containing the static
 * insole template: the clipped background fill + the rounded-rect outline.
 * This is drawn once per (footWidth, footHeight) pair and reused every frame.
 */
function getInsoleTemplate(
  footWidth: number,
  footHeight: number
): HTMLCanvasElement | null {
  const key = `${footWidth}x${footHeight}`;
  let canvas = templateCache.get(key);
  if (canvas) return canvas;

  canvas = document.createElement('canvas');
  canvas.width = footWidth;
  canvas.height = footHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const layout = getInsoleLayout(footWidth, footHeight);
  const rectX = layout.offsetX + layout.padding;
  const rectY = layout.offsetY + layout.padding;
  const rectW = footWidth - 2 * layout.offsetX - 2 * layout.padding;
  const rectH = footHeight - 2 * layout.offsetY - 2 * layout.padding;

  // Background fill (clipped to insole shape)
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(rectX, rectY, rectW, rectH, 20);
  ctx.clip();
  ctx.fillStyle = 'rgba(10, 10, 15, 0.8)';
  ctx.fillRect(0, 0, footWidth, footHeight);
  ctx.restore();

  // Outline stroke
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(rectX, rectY, rectW, rectH, 20);
  ctx.stroke();

  templateCache.set(key, canvas);
  return canvas;
}

/** Read a CSS variable from the document and return a fallback if missing. */
function readCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name);
  return value.trim() || fallback;
}

export default function PressureHeatmap({
  pressureArray,
  colorScheme,
  width,
  height,
}: PressureHeatmapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // rAF batching: schedule the render for the next animation frame. If the
    // effect fires again before the rAF (e.g., pressureArray changes at 100 Hz
    // while the display refreshes at 60 Hz), the cleanup cancels the previous
    // rAF and a new one is scheduled with the latest props. This coalesces
    // multiple updates into a single render per frame.
    const rafId = requestAnimationFrame(() => {
      renderHeatmap(ctx, pressureArray, colorScheme, width, height);
    });

    return () => cancelAnimationFrame(rafId);
  }, [pressureArray, colorScheme, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: 'block', width, height }}
      aria-label="压力热力图"
    />
  );
}

// ---------------------------------------------------------------------------
// Render logic (executed inside rAF)
// ---------------------------------------------------------------------------

/**
 * Render the full heatmap: two feet + legend.
 * Extracted as a standalone function so it runs inside the rAF callback
 * without capturing stale closures.
 */
function renderHeatmap(
  ctx: CanvasRenderingContext2D,
  pressureArray: DualPressureArray | null,
  colorScheme: ColorSchemeId,
  width: number,
  height: number
): void {
  // Use the cached LUT (avoids 256-entry rebuild every frame).
  const lut = getCachedLUT(colorScheme);
  const scheme = COLOR_SCHEMES[colorScheme];

  // Layout: two insoles stacked vertically + legend at the bottom.
  const legendHeight = 36;
  const gap = 12;
  const footHeight = Math.max(40, (height - legendHeight - gap * 2) / 2);
  const footWidth = Math.min(width - 16, footHeight * 2.7);

  // Clear the whole canvas.
  ctx.clearRect(0, 0, width, height);

  if (!pressureArray) {
    // Empty state: show placeholder text.
    ctx.fillStyle = readCssVar('--text-tertiary', '#6B6B80');
    ctx.font = '14px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('暂无压力数据', width / 2, height / 2);
    return;
  }

  // Render left foot (top) and right foot (bottom, Y-mirrored).
  renderFoot(ctx, pressureArray.left.points, lut, footWidth, footHeight, gap, false);
  renderFoot(ctx, pressureArray.right.points, lut, footWidth, footHeight, gap + footHeight + gap, true);

  // Foot labels.
  ctx.fillStyle = readCssVar('--text-secondary', '#B4B4C7');
  ctx.font = '12px Inter, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('左脚', 8, gap + 2);
  ctx.fillText('右脚', 8, gap + footHeight + gap + 2);

  // Legend at bottom: "低(蓝) → 高(红)" with 4 color stops.
  renderLegend(ctx, scheme, lut, width, height, legendHeight);
}

/**
 * Render one foot: draw the cached insole template (background + outline),
 * then draw the pressure point gradients on top (clipped), then the COP dot.
 *
 * @param mirrorY  When true, mirror the Y axis (right foot). This avoids
 *                 allocating a new mirrored array each frame.
 */
function renderFoot(
  ctx: CanvasRenderingContext2D,
  points: PressurePoint[],
  lut: Uint8ClampedArray,
  footWidth: number,
  footHeight: number,
  originY: number,
  mirrorY: boolean
): void {
  ctx.save();
  ctx.translate((ctx.canvas.width - footWidth) / 2, originY);

  // Draw the cached insole template (background + outline).
  const template = getInsoleTemplate(footWidth, footHeight);
  if (template) {
    ctx.drawImage(template, 0, 0);
  }

  const layout = getInsoleLayout(footWidth, footHeight);
  const rectX = layout.offsetX + layout.padding;
  const rectY = layout.offsetY + layout.padding;
  const rectW = footWidth - 2 * layout.offsetX - 2 * layout.padding;
  const rectH = footHeight - 2 * layout.offsetY - 2 * layout.padding;

  // Draw pressure points (clipped to insole shape).
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(rectX, rectY, rectW, rectH, 20);
  ctx.clip();

  const pointRadius = 14;
  const smoothing = 1.8;
  const radius = pointRadius * smoothing;

  for (const point of points) {
    const py = mirrorY ? -point.y : point.y;
    const cx = layout.offsetX + (point.x - INSOLE_BOUNDS.minX) * layout.scale;
    const cy = layout.offsetY + (py - INSOLE_BOUNDS.minY) * layout.scale;
    const [r, g, b, a] = getColorFromLUT(lut, point.value);

    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    gradient.addColorStop(0, `rgba(${r},${g},${b},${a / 255})`);
    gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // COP overlay: white dot at the pressure center.
  // Compute COP from the original points (no copy), negate Y for right foot.
  const cop = calculateCop(points);
  if (cop) {
    const copY = mirrorY ? -cop.y : cop.y;
    const cx = layout.offsetX + (cop.x - INSOLE_BOUNDS.minX) * layout.scale;
    const cy = layout.offsetY + (copY - INSOLE_BOUNDS.minY) * layout.scale;

    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 6;
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  ctx.restore();
}

/** Render the legend bar with 4 color stops and labels. */
function renderLegend(
  ctx: CanvasRenderingContext2D,
  scheme: { stops: readonly [number, string][] },
  lut: Uint8ClampedArray,
  width: number,
  height: number,
  legendHeight: number
): void {
  const legendY = height - legendHeight + 8;
  const legendW = Math.min(220, width - 40);
  const legendX = (width - legendW) / 2;
  const stops = scheme.stops as [number, string][];

  // Gradient bar.
  const grad = ctx.createLinearGradient(legendX, 0, legendX + legendW, 0);
  for (const [pos, color] of stops) {
    grad.addColorStop(pos, color);
  }
  ctx.fillStyle = grad;
  ctx.fillRect(legendX, legendY, legendW, 10);

  // Border around legend bar.
  ctx.strokeStyle = 'rgba(255,255,255,0.15)';
  ctx.lineWidth = 1;
  ctx.strokeRect(legendX, legendY, legendW, 10);

  // 4 color stop markers + labels.
  ctx.fillStyle = readCssVar('--text-tertiary', '#6B6B80');
  ctx.font = '10px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const samplePositions = [0, 0.33, 0.66, 1];
  for (const t of samplePositions) {
    const x = legendX + t * legendW;
    const [r, g, b] = getColorFromLUT(lut, t);
    // Tick mark.
    ctx.strokeStyle = `rgba(${r},${g},${b},1)`;
    ctx.beginPath();
    ctx.moveTo(x, legendY + 10);
    ctx.lineTo(x, legendY + 14);
    ctx.stroke();
    // Value label.
    ctx.fillStyle = readCssVar('--text-tertiary', '#6B6B80');
    ctx.fillText(t.toFixed(2), x, legendY + 16);
  }

  // Legend end labels.
  ctx.textAlign = 'left';
  ctx.fillText('低(蓝)', legendX, legendY + 26);
  ctx.textAlign = 'right';
  ctx.fillText('高(红)', legendX + legendW, legendY + 26);
}
